import { useState, ReactElement } from 'react';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import { getUserAvatar } from 'api/get';
import { isMobile } from 'utils/helper';
import UserInterface from 'interface/user';
/* Components */
import {
  Avatar,
  Box,
  ClassNameMap,
  IconButton,
  Menu,
  MenuItem,
  Popover,
} from '@mui/material';
import {
  AccountCircle,
  AssignmentOutlined,
  Menu as MenuIcon,
} from '@mui/icons-material';
import AllMySdsIcon from 'assets/icons/AllMySds';
import Logo from 'assets/icons/Logo';
import PopoverItem from './popover-menu-item';

interface NavListItem {
  id: string;
  title: string;
  to: string;
  icon: ReactElement;
}

const Header = () => {
  const { t } = useTranslation(['header', 'languages']);
  const history = useHistory();
  const classes: ClassNameMap = useStyles();
  const userInfo: UserInterface = useSelector(
    (state: any) => state.pageReducer.userInfo
  );
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const navList: Array<NavListItem> = [
    {
      id: 'our-components',
      title: t('header:our_components'),
      to: '/our-components/',
      icon: (
        <AssignmentOutlined
          sx={{
            width: '24px',
            color: isMobile() ? '#424B5A' : 'white',
            mr: '10px',
          }}
        />
      ),
    },
    {
      id: 'our-sds',
      title: t('header:our_sds'),
      to: '/',
      icon: (
        <AllMySdsIcon
          width={26}
          height={26}
          color={isMobile() ? '#424B5A' : 'white'}
          style={{ marginRight: '10px' }}
        />
      ),
    },
  ];

  const handleCloseNavMenu = (): void => {
    setAnchorElNav(null);
  };

  const renderNavList = (): ReactElement => {
    if (isMobile()) {
      return (
        <Box sx={{ display: 'flex' }}>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={e => setAnchorElNav(e.currentTarget)}
            color="inherit"
          >
            <MenuIcon />
          </IconButton>

          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            keepMounted
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{ display: 'block' }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {navList.map((item: NavListItem) => (
              <MenuItem
                key={`nav-item-${item.id}`}
                onClick={handleCloseNavMenu}
              >
                <NavLink style={{ textDecoration: 'none' }} to={item.to}>
                  <Box className={classes.headerLink} sx={{ color: '#424B5A' }}>
                    {item.icon}
                    {item.title}
                  </Box>
                </NavLink>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      );
    }

    return (
      <Box sx={{ gap: '20px', display: 'flex' }}>
        {navList.map((item: NavListItem) => (
          <NavLink
            key={`nav-item-${item.id}`}
            style={{ textDecoration: 'none' }}
            to={item.to}
          >
            <Box className={classes.headerLink} sx={{ color: 'white' }}>
              {item.icon}
              {item.title}
            </Box>
          </NavLink>
        ))}
      </Box>
    );
  };

  return (
    <div className={classes.header}>
      <Box className={classes.headerNav}>
        {!isMobile() && (
          <Logo className={classes.logoImg} onClick={() => history.push('/')} />
        )}
        {renderNavList()}
      </Box>

      {isMobile() && (
        <Logo
          className={classes.mobileLogo}
          onClick={() => history.push('/')}
        />
      )}

      <Box sx={{ marginRight: { xs: '10px', md: '20px' } }}>
        <IconButton
          onClick={e => setAnchorElUser(e.currentTarget)}
          sx={{ p: 0 }}
        >
          {userInfo && userInfo.avatar ? (
            <Avatar alt="avatar" src={getUserAvatar(userInfo.avatar)} />
          ) : (
            <AccountCircle
              sx={{ color: '#FFF', width: '40px', height: '40px' }}
            />
          )}
        </IconButton>

        <Popover
          sx={{ mt: '45px' }}
          id="menu-appbar"
          anchorEl={anchorElUser}
          keepMounted
          open={Boolean(anchorElUser)}
          onClose={() => setAnchorElUser(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <PopoverItem onClose={() => setAnchorElUser(null)} />
        </Popover>
      </Box>
    </div>
  );
};

export default Header;
